// Shapes the Job Automation screens read from the API (services/api/src/ja/engine.ts, runs.ts).

export type RunStatus = "running" | "finishing" | "paused" | "attention" | "stopped" | "done";
export type NodeState = "waiting" | "notApplicable" | "sending" | "running" | "done" | "failed" | "skipped";

export interface RunReport {
  name: string;
  requestName: string;
  state: "waiting" | "running" | "locating" | "done" | "failed" | "skipped";
  /** Its branch in the tree, e.g. CLOSE_PAYROLL_REPORT_r1. */
  branch?: string;
  requestId?: string;
  userReport?: string;
  archive?: { key: string; state: "waiting" | "done" | "failed"; error?: string };
  message?: string;
}

export interface RunPath {
  id: string;
  name: string;
  critical: boolean;
  startedBy: string;
  nodes: number[];
  pos: number;
  state: "notStarted" | "running" | "paused" | "attention" | "done" | "skipped" | "stopped";
  pause?: { reason: "break" | "failed" | "debug"; node: number; message: string };
}

export interface RunNode {
  /** The step branch it's in; unset on the trunk. */
  path?: string;
  stepId: string;
  seq: number;
  name: string;
  jobName?: string;
  /** Debug mode: where the step's functions stopped, and the Infor calls the stopped attempt made. */
  debug?: { phase: "pre" | "fields" | "check" | "post"; skip: number; at: { fn: string; line: number; hit: number; vars: Record<string, string>; stack: string }; calls?: string[] };
  /** One pass of a repeated step (multiple runs, or a loop). */
  repeat?: { index: number; of: number; row?: number; loop?: boolean; last?: boolean };
  state: NodeState;
  attempt: number;
  fields?: { name: string; value: string }[];
  requestId?: string;
  workunit?: string;
  submittedAt?: string;
  checks: number;
  lastCheck?: string;
  warnings: string[];
  checkNote?: string;
  watch?: string[];
  spawned?: { id: string; name: string; cls: string; action: string; state: "running" | "done" | "failed" | "unwatched"; message?: string }[];
  rerun?: { field: string; mode: RerunMode; flag?: string; required?: boolean; value?: boolean; why?: string };
  error?: string;
  uncertain?: boolean;
  timedOut?: boolean;
  note?: string;
  /** Compass SQL steps (design §15.9.5): the query Compass started and what it ran with. */
  compass?: { entry: string; version: number; params: Record<string, string>; queryId?: string; status?: string };
  /** RADAR steps (design §15.9.5): the RADAR job the step started. */
  radar?: { jobId: string; op: string; queryId?: string; status?: string; outputBytes?: number; push?: { printManagerDocumentId?: string; userReportId?: string; distributed?: boolean }; usage?: Record<string, number | string> };
  /** Replication steps: LastRefreshStamp before the step (baseline) and once it finished. */
  baseline?: string;
  refreshedAt?: string;
  /** What a waiting step waits on (its waitFor). */
  waitingOn?: string;
  startedAt?: string;
  endedAt?: string;
  reports: RunReport[];
}

export interface Run {
  runId: string;
  automationId: string;
  type: string;
  name: string;
  version: number;
  connId: string;
  values: Record<string, string>;
  runKey: string;
  seq: number;
  /** Reports that failed or were let go: complete with exceptions. */
  exceptions?: number;
  status: RunStatus;
  open: boolean;
  pause?: { reason: "break" | "failed" | "debug"; node: number; message: string; path?: string; scope?: "run" };
  /** Debug mode: debugger lines in the step functions pause the run. */
  debug?: boolean;
  /** Step branches: each a lane with its own position, state and pause. */
  paths?: RunPath[];
  cursor: number;
  nodes: RunNode[];
  startedBy: string;
  startedAt: string;
  updatedAt: string;
  endedAt?: string;
}

export interface RunSummary {
  runId: string;
  automationId: string;
  type: string;
  name: string;
  version: number;
  seq: number;
  runKey: string;
  status: RunStatus;
  open: boolean;
  current?: string;
  startedBy: string;
  startedAt: string;
  updatedAt: string;
  endedAt?: string;
}

export interface Prompt {
  code: string;
  label: string;
  legacyName?: string;
  kind: "text" | "date" | "select";
  required: boolean;
  default?: string;
  options?: { value: string; label: string }[];
  lookup?: { builtin: "paymentSchedules" | "payPeriodEnds"; fills?: { prompt: string; from: string }[] };
  setBy?: string;
  maxLength?: number;
}

export interface StepDef {
  id: string;
  seq: number;
  name: string;
  kind: string;
  infor?: { class: string; action: string; submit: string };
  radar?: { op: string };
  when?: { prompt: string; equals: string };
  pre?: string[];
  breakAfter: boolean;
  reports: { name: string }[];
  ownReport?: string;
  repeat?: { array: string; where?: { attr: string; equals: string } };
  loop?: { max: number };
}

export type RerunMode = "auto" | "yes" | "no";
export interface RerunOption {
  field: string;
  default: RerunMode;
  flag?: string;
  required: boolean;
  source: "file" | "waypoint";
}

export interface Definition {
  id: string;
  type: string;
  name: string;
  version: number;
  runKey: string[];
  runDatePrompt: string;
  prompts: Prompt[];
  trunk: StepDef[];
  arrays?: { name: string; rows: { value: string; attrs: Record<string, string> }[] }[];
}

/** How many jobs a step becomes: one per matching array row, up to its loop's max, or one (the engine's planNodes). */
export function passes(def: Definition, s: StepDef, values: Record<string, string>) {
  if (s.loop) return Math.max(1, Math.min(200, Math.floor(s.loop.max)));
  if (!s.repeat) return 1;
  const a = def.arrays?.find((x) => x.name === s.repeat!.array);
  const w = s.repeat.where;
  const want = w ? (Object.prototype.hasOwnProperty.call(values, w.equals) ? values[w.equals] : w.equals) : undefined;
  return (a?.rows ?? []).filter((r) => !w || (r.attrs[w.attr] ?? "") === want).length;
}

export const STATUS_LABEL: Record<RunStatus, string> = { running: "Running", finishing: "Finishing reports", paused: "Waiting at a break", attention: "Needs a decision", stopped: "Stopped", done: "Complete" };
export const STATUS_TONE: Record<RunStatus, string> = { running: "bg-accent-soft text-accent-text", finishing: "bg-accent-soft text-accent-text", paused: "bg-warn-soft text-warn", attention: "bg-bad-soft text-bad", stopped: "bg-surface-2 text-muted", done: "bg-good-soft text-good" };
export const NODE_LABEL: Record<NodeState, string> = { waiting: "Waiting", notApplicable: "Not this run", sending: "Sending", running: "Running", done: "Complete", failed: "Failed", skipped: "Skipped" };
export const NODE_TONE: Record<NodeState, string> = { waiting: "text-muted", notApplicable: "text-muted", sending: "text-accent-text", running: "text-accent-text", done: "text-good", failed: "text-bad", skipped: "text-warn" };

/** Run keys are stored positionally (org1=org2=org3=org4=date); show the parts that are set. */
export const keyLabel = (k: string) => k.split("=").filter(Boolean).join(" · ");
export const when = (iso?: string) => (iso ? new Date(iso).toLocaleString() : "");
export const time = (iso?: string) => (iso ? new Date(iso).toLocaleTimeString() : "");
